/**
 * PhotoCard - Individual photo card in the Overview grid.
 *
 * Tank Redesign port: thumbnail card matches
 * `~/Downloads/tank-redesign/screens-base.jsx § Screen02Overview` grid —
 * 3:2 thumbnail, redesign StatusPill in top-right, mono filename,
 * subtle meta line, hover-tinted background.
 */

import { useState } from 'react';
import {
  statusColors,
  getDisplayStatus,
  getStatusColor,
  getStatusLabel,
  getStatusTooltip,
  getRedesignStatusKind,
  getOverviewTileKey,
  OVERVIEW_TILE_LABEL,
  OVERVIEW_TILE_KIND,
} from '../../utils/statusUtils';
import { StatusPill as RedesignStatusPill } from '../../shared/components/redesign';

// Pipeline statuses where no image exists yet
const noImageStatuses = new Set(["ingested", "processing"]);

// Pipeline statuses where face info is not yet available
// (detecting/identifying deprecated but kept for historical data)
const noFaceInfoStatuses = new Set(["ingested", "processing", "detecting", "identifying"]);

// Statuses considered final (no blur applied)
const finalStatuses = new Set([
  "done",
  "pending",
  "attention",
  "recropping",
  "exported",
  "archived",
]);

// Statuses that indicate intermediate processing
const intermediateStatuses = new Set(["processing", "detecting", "identifying"]);

// Get status-appropriate placeholder message. Pre-pipeline statuses use
// the same INCOMING ● cue as the Processing tile so the grid signals
// that a fresh upload is mid-flight rather than missing.
const getPlaceholderMessage = (status) => {
  const messages = {
    ingested: "INCOMING",
    processing: "INCOMING",
    detecting: "INCOMING", // DEPRECATED
    identifying: "INCOMING", // DEPRECATED
    error: "Error occurred",
  };
  return messages[status] || "Loading preview...";
};

// Status pill inline component - uses shared statusUtils for consistency
const StatusPill = ({ photo }) => {
  if (!photo?.status) return null;

  const color = getStatusColor(photo);
  const label = getStatusLabel(photo);
  const tooltip = getStatusTooltip(photo);

  return (
    <span
      className="status-pill"
      style={{ borderColor: color, color }}
      title={tooltip}
    >
      <span className="dot" style={{ background: color }} /> {label}
    </span>
  );
};

/**
 * Check if photo has face statistics available.
 */
const hasFaceStats = (photo) =>
  Number.isFinite(photo.face_count) ||
  Number.isFinite(photo.labeled_count) ||
  Number.isFinite(photo.unlabeled_count);

/**
 * Compute blur amount based on photo status.
 * Final statuses show with no blur, pipeline statuses show with blur.
 */
const computeBlur = (photo) => {
  if (finalStatuses.has(photo.status)) return 0;
  if (hasFaceStats(photo)) return 4;
  if (photo.filename || intermediateStatuses.has(photo.status)) return 8;
  return 12;
};

/**
 * Format a single person's display name with optional jersey number.
 */
const formatPersonName = (person) => {
  const name =
    person.display_name ||
    `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return person.jersey_number ? `${name} #${person.jersey_number}` : name;
};

/**
 * Get display names for persons in photo.
 */
const getPersonNames = (photo) => {
  if (!photo.persons || photo.persons.length === 0) return null;
  return photo.persons.map(formatPersonName).filter(Boolean).join(", ");
};

/**
 * Determine the navigation target for a photo click.
 */
const resolveClickTarget = (getClickTarget, photo) => {
  if (typeof getClickTarget === "function") {
    return getClickTarget(photo);
  }
  return "/edit";
};

/**
 * Resolve the image URL based on display mode.
 * Always uses preview_url; cropped view is handled client-side via CSS.
 */
const resolveImageUrl = (photo, multiMode, showViz, resolveUrl, withCache, cacheKey) => {
  return withCache(resolveUrl(photo.preview_url), cacheKey);
};

/**
 * Compute blur filter style string.
 */
const getBlurStyle = (blur) => (blur === 0 ? "none" : `blur(${blur}px)`);

/**
 * Generate the fallback background gradient style.
 */
const getFallbackStyle = (blur) => ({
  filter: getBlurStyle(blur),
  background: "linear-gradient(135deg, rgba(60,90,70,0.6), rgba(40,60,50,0.5))",
});

/**
 * Compute image opacity based on load state.
 */
const computeImageOpacity = (loaded, broken) => {
  if (broken) return 0;
  if (loaded) return 1;
  return 0.1;
};

/**
 * Build article className from mode and selection state.
 */
const buildCardClassName = (multiMode, isSelected) => {
  const classes = ["thumb-card"];
  if (multiMode) classes.push("multi-mode");
  if (isSelected) classes.push("selected");
  return classes.join(" ");
};

/**
 * Compute image key for React reconciliation.
 */
const computeImageKey = (imageId, cacheKey, showViz) =>
  `${imageId}-${cacheKey}-${showViz ? "viz" : "edit"}`;

/**
 * Multi-select checkbox component.
 */
const MultiCheckbox = ({ isSelected }) => (
  <div className={`multi-checkbox ${isSelected ? "checked" : ""}`}>
    {isSelected && <span className="check-mark" />}
  </div>
);

/**
 * Loading fallback overlay component.
 */
const LoadingFallback = ({ status, blur, isOverlay }) => {
  const baseStyle = getFallbackStyle(blur);
  const style = isOverlay
    ? {
        ...baseStyle,
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }
    : baseStyle;

  return (
    <div className="thumb-fallback" style={style}>
      {getPlaceholderMessage(status)}
    </div>
  );
};

/**
 * Thumbnail image with fallback and optional client-side CSS crop.
 * When showCrop is true and crop_info is available, uses CSS background-image
 * to show the cropped region from the preview image (no extra server request).
 */
const ThumbnailImage = ({
  photo,
  imageUrl,
  imageKey,
  blur,
  loaded,
  broken,
  showCrop,
  onImageLoad,
  onImageError,
}) => {
  const showFallback = !loaded || broken;
  const cropBox = showCrop ? photo.crop_info?.box : null;
  const imgW = photo.crop_info?.image_width;
  const imgH = photo.crop_info?.image_height;
  const useCssCrop = cropBox && imgW && imgH && loaded && !broken;

  let cropStyle = null;
  if (useCssCrop) {
    const cropW = cropBox[2] - cropBox[0];
    const cropH = cropBox[3] - cropBox[1];

    // Uniform scale so the crop covers the 3:2 container without distortion.
    // .thumb-media is 3:2 (styles.css:790–795); the constants below are
    // baked-in for that ratio. Aspect-ratio constraint:
    //   bgSizeY = bgSizeX * 3*imgH / (2*imgW)
    // (Was previously 4:3 — left over from an earlier card design before
    // the redesign moved the thumbnail to 3:2. Caused Fit-mode cards to
    // render as a thin horizontal strip with empty space below.)
    const fitW = imgW / cropW;
    const fitH = (2 * imgW) / (3 * cropH);
    const bgSizeX = Math.max(fitW, fitH) * 100;
    const bgSizeY = bgSizeX * (3 * imgH) / (2 * imgW);

    // Visible image area at this scale
    const visW = imgW * 100 / bgSizeX;
    const visH = imgH * 100 / bgSizeY;

    // Center the visible area on the crop, clamped to image bounds
    const cx = cropBox[0] + cropW / 2;
    const cy = cropBox[1] + cropH / 2;
    const left = Math.max(0, Math.min(imgW - visW, cx - visW / 2));
    const top = Math.max(0, Math.min(imgH - visH, cy - visH / 2));
    const posX = imgW > visW ? (left / (imgW - visW)) * 100 : 0;
    const posY = imgH > visH ? (top / (imgH - visH)) * 100 : 0;

    cropStyle = {
      backgroundSize: `${bgSizeX}% ${bgSizeY}%`,
      backgroundPosition: `${posX}% ${posY}%`,
    };
  }

  return (
    <>
      {useCssCrop ? (
        <div
          key={imageKey}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${imageUrl})`,
            ...cropStyle,
            backgroundRepeat: 'no-repeat',
            filter: getBlurStyle(blur),
            transition: 'filter 200ms ease',
          }}
        />
      ) : (
        <img
          key={imageKey}
          src={imageUrl}
          alt={photo.filename || "preview"}
          onError={onImageError}
          onLoad={onImageLoad}
          style={{
            opacity: computeImageOpacity(loaded, broken),
            filter: getBlurStyle(blur),
            transition: "filter 200ms ease",
          }}
        />
      )}
      {showFallback && (
        <LoadingFallback status={photo.status} blur={blur} isOverlay />
      )}
    </>
  );
};

/**
 * Face info subline component.
 */
const FaceInfoSubline = ({ photo }) => {
  const unlabeledCount = photo.unlabeled_count ?? 0;
  const unlabeledText = unlabeledCount > 0 ? ` [${unlabeledCount} unlabeled]` : "";

  return (
    <div className="subline">
      Faces {photo.labeled_count ?? "?"} / {photo.face_count ?? "?"}
      {unlabeledText}
    </div>
  );
};

/**
 * Person names or face info subline.
 */
const PersonOrFaceSubline = ({ photo, personNames }) => {
  if (personNames) {
    return (
      <div className="subline persons-line" title={personNames}>
        {personNames}
      </div>
    );
  }

  if (noFaceInfoStatuses.has(photo.status)) {
    return null;
  }

  return <FaceInfoSubline photo={photo} />;
};

function PhotoCard({
  photo,
  showViz,
  multiMode,
  isSelected,
  onSelect,
  onNavigate,
  imageLoader,
  activeFilters,
  resolveUrl,
  withCache,
  cacheKeyFor,
  // Permission-based navigation: function(photo) => "/edit"
  getClickTarget,
  // When true, the thumb-media keeps the photo's natural aspect
  // (e.g. 3:2) instead of the equal-height 4:3 crop. Used by the
  // Overview Masonry layout so 3:2 frames don't crop awkwardly.
  naturalAspect = false,
}) {
  const { isLoaded, isBroken, onLoad, onError } = imageLoader;
  const cacheKey = cacheKeyFor(photo);
  const blur = computeBlur(photo);
  const personNames = getPersonNames(photo);
  const loaded = isLoaded(photo.image_id, cacheKey);
  const broken = isBroken(photo.image_id, cacheKey);
  const hasImage = !noImageStatuses.has(photo.status) && photo.preview_url;
  const imageUrl = resolveImageUrl(photo, multiMode, showViz, resolveUrl, withCache, cacheKey);
  const imageKey = computeImageKey(photo.image_id, cacheKey, showViz);

  const handleClick = () => {
    if (multiMode) {
      onSelect(photo.image_id);
      return;
    }
    // When no filters active (viewing "total"), pass "all" to show all photos
    const filtersToPass = activeFilters?.length ? activeFilters : ["all"];
    const target = resolveClickTarget(getClickTarget, photo);
    onNavigate?.(target, photo.image_id, undefined, filtersToPass);
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    handleClick();
  };

  const handleImageError = () => onError(photo.image_id, cacheKey);

  // Capture the rendered image's true aspect ratio from the browser's
  // EXIF-corrected naturalWidth/naturalHeight. This is the only reliable
  // source for portrait masonry: `crop_info.image_*` reports raw sensor
  // dimensions (landscape) for EXIF-rotated portrait JPEGs, so a
  // crop_info-derived aspect reserves a landscape slot for a visually
  // portrait card. The browser's naturalWidth/Height respects EXIF
  // orientation and returns the rendered dimensions.
  const [measuredAspect, setMeasuredAspect] = useState(null);
  const handleImageLoad = (e) => {
    const img = e?.currentTarget;
    if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setMeasuredAspect(img.naturalWidth / img.naturalHeight);
    }
    onLoad(photo.image_id, cacheKey);
  };

  // Inline aspect-ratio for the thumbnail box. Always set, in both Fit
  // and Masonry modes — relying on a CSS rule (.thumb-media { aspect-
  // ratio: 3/2 }) was empirically unreliable; some cascade somewhere is
  // overriding it intermittently. Inline style wins regardless.
  //   - Fit  (naturalAspect=false): always 3/2 ("Fit" means crop to 3:2,
  //     so portrait photos still get a landscape slot — that's the
  //     semantic of Fit mode).
  //   - Masonry (naturalAspect=true): measuredAspect from the loaded
  //     image (EXIF-rotation-aware) wins; crop_info-derived comes
  //     second (early frame before image loads), 3/2 last.
  const masonryAspect =
    measuredAspect
    ?? (photo.crop_info?.image_width && photo.crop_info?.image_height
      ? photo.crop_info.image_width / photo.crop_info.image_height
      : 1.5);
  const thumbMediaStyle = {
    aspectRatio: naturalAspect ? masonryAspect : 1.5,
  };

  return (
    <article
      className={buildCardClassName(multiMode, isSelected)}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {multiMode && <MultiCheckbox isSelected={isSelected} />}

      <div
        className={`thumb-media ${naturalAspect ? 'thumb-media--natural' : ''}`}
        style={thumbMediaStyle}
      >
        {hasImage ? (
          <ThumbnailImage
            photo={photo}
            imageUrl={imageUrl}
            imageKey={imageKey}
            blur={blur}
            loaded={loaded}
            broken={broken}
            // Disable the CSS crop in masonry: the whole point of
            // masonry is to show the full natural frame.
            showCrop={!multiMode && !showViz && !naturalAspect}
            onImageLoad={handleImageLoad}
            onImageError={handleImageError}
          />
        ) : (
          <LoadingFallback
            status={photo.status}
            blur={blur}
            isOverlay={false}
          />
        )}
        <span className="status-pill-overlay" title={getStatusTooltip(photo)}>
          {(() => {
            const tileKey = getOverviewTileKey(photo);
            return (
              <RedesignStatusPill kind={OVERVIEW_TILE_KIND[tileKey]} compact>
                {OVERVIEW_TILE_LABEL[tileKey]}
              </RedesignStatusPill>
            );
          })()}
        </span>
      </div>

      <div className="thumb-meta">
        <div className="name">{photo.filename || "Loading..."}</div>
        <PersonOrFaceSubline photo={photo} personNames={personNames} />
      </div>
    </article>
  );
}

// Re-export status utilities for backward compatibility
export { statusColors, getDisplayStatus, getStatusColor, getStatusLabel };
export { getPlaceholderMessage, StatusPill, computeBlur };
export default PhotoCard;
